import styled from 'styled-components';
import COLORS from '../../style/universal.color';
import { MLBTheme } from '../../style/theme.types';

const { mlbGrey2, mlbGrey4 } = COLORS;

interface TableProps {
  tableFixed: boolean;
  dark: boolean;
  paddingConfig: TableCellPaddingConfig;
  hideDividers: boolean;
  tfootExists: boolean;
  hoverRowHighlight: boolean;
  highlightColor: string;
  underlinedColor: string;
  theme: MLBTheme;
}

interface TableScrollerProps {
  divWidth: string;
  dark: boolean;
  hideDividers: boolean;
  leftPadding?: string;
  rightPadding?: string;
}

export interface TableCellPaddingConfig {
  bodyTdPadding: string;
  bodyThPadding: string;
  headThPadding: string;
}

interface TableContainerProps {
  containerWidth: string;
  containerMaxWidth?: string;
}

export const StyledTable = styled.table<TableProps>`
  ${(props) => (props.tableFixed ? 'table-layout: fixed;' : '')}
  font-family: ${({ theme = {} }) => theme.fonts?.family?.primary};
  width: 100%;
  padding-bottom: 5px;
  border-collapse: collapse;
  overflow-x: hidden;

  thead {
    ${({ dark, hideDividers }) => (!hideDividers ? `border-bottom: 1px solid ${dark ? mlbGrey4 : mlbGrey2};` : '')}
    margin-bottom: 20px;
    th {
      ${({ hideDividers }) => (hideDividers ? `border-bottom: none;` : '')}
      padding: ${({ paddingConfig }) => paddingConfig.headThPadding};
    }
  }

  tbody {
    th {
      padding: ${({ paddingConfig }) => paddingConfig.bodyThPadding};
    }
    td {
      padding: ${({ paddingConfig }) => paddingConfig.bodyTdPadding};
    }

    tr {
      &[data-underlined='true'] {
        border-bottom: 1px ${({ underlinedColor }) => underlinedColor} dotted;

        th {
          border-bottom: 1px ${({ underlinedColor }) => underlinedColor} dotted;
        }
      }
    }

    ${({ hoverRowHighlight, highlightColor }) =>
      hoverRowHighlight
        ? `
      tr {
        &:hover {
          background-color: ${highlightColor};
          border-radius: 4px;
          th {
            background-color: ${highlightColor};
          }
          td, th {
            border-radius: 0px;
            &:first-child {
              border-top-left-radius: 4px;
              border-bottom-left-radius: 4px;
            }
            &:last-child {
              border-top-right-radius: 4px;
              border-bottom-right-radius: 4px;
            }
          }
        }
      }

    `
        : ''}

    ${({ hideDividers, tfootExists }) =>
      !hideDividers
        ? `
        &:before {
          width: 100%;
          content: '';
          display: block;
          line-height: 10px;
          height: 5px;
        }
        ${
          tfootExists
            ? `&:after {
            content: '';
            display: block;
            line-height: 10px;
            height: 5px;
          }`
            : ''
        }
    `
        : ''}

    &:not(:first-child) {
      &:before {
        height: 0;
      }
    }
  }

  tfoot {
    th {
      padding: ${({ paddingConfig }) => paddingConfig.bodyThPadding};
    }
    td {
      padding: ${({ paddingConfig }) => paddingConfig.bodyTdPadding};
    }
    ${({ dark, hideDividers }) => (!hideDividers ? `border-top: 1px solid ${dark ? mlbGrey4 : mlbGrey2};` : '')}
  }
`;

export const TableScroller = styled.div<TableScrollerProps>`
  overflow-x: auto;
  overflow-y: hidden;
  box-sizing: border-box;
  width: ${(props) => `calc(${props.divWidth} - ${props.leftPadding} - ${props.rightPadding})`};
  display: inline-block;
  margin-right: ${(props) => props.rightPadding};
  margin-left: ${({ leftPadding }) => leftPadding};
`;

export const TableContainer = styled.div<TableContainerProps>`
  width: ${({ containerWidth }) => containerWidth};
  ${({ containerMaxWidth }) => `max-width: ${containerMaxWidth}`};
  position: relative;
  display: inline-block;
  overflow: hidden;
`;
